'use client';
import {
  connectorsForWallets,
  darkTheme,
  RainbowKitProvider,
} from '@rainbow-me/rainbowkit';
import {
  argentWallet,
  coinbaseWallet,
  injectedWallet,
  ledgerWallet,
  metaMaskWallet,
  rainbowWallet,
  trustWallet,
  walletConnectWallet,
  rabbyWallet,
  safeWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { ReactNode, useMemo } from 'react';
import { Chain, configureChains, WagmiConfig, createConfig } from 'wagmi';
import { mainnet, arbitrum, base } from 'viem/chains';
import { jsonRpcProvider } from 'wagmi/providers/jsonRpc';
import { publicProvider } from 'wagmi/providers/public';
import { getChainConstant, Network } from 'nftx.js';

export const chains = { mainnet, arbitrum, base };

const projectId = '9cf4c0e820d6eba1dd101accb815e76e';

const RPC_URL = {
  // [Network.Mainnet]: 'https://rpc.nftx.xyz',
  [Network.Mainnet]: 'https://eth-mainnet.nftx.xyz/a/shahv8futobaiwop',
  [Network.Arbitrum]: 'https://arb-mainnet.nftx.xyz/a/fsvt33mzxjcxjdjw',
  [Network.Base]: 'https://base-mainnet.nftx.xyz/a/chaepohghi0nep9i',
};

const getProviders = (network: number) => {
  const providers: any[] = [];

  providers.push(
    jsonRpcProvider({
      rpc: () => ({
        http: getChainConstant(RPC_URL, network, 'https://rpc.nftx.xyz'),
      }),
    })
  );

  providers.push(publicProvider());

  return providers;
};

const getConnectors = (chains: Chain[]) => {
  return connectorsForWallets([
    {
      groupName: 'Suggested',
      wallets: [
        safeWallet({ chains }),
        injectedWallet({ chains }),
        rabbyWallet({ chains }),
        metaMaskWallet({ chains, projectId }),
        rainbowWallet({ chains, projectId }),
        walletConnectWallet({ chains, projectId }),
        coinbaseWallet({ chains, appName: 'NFTX' }),
      ],
    },
    {
      groupName: 'Other',
      wallets: [
        ledgerWallet({ chains, projectId }),
        argentWallet({ chains, projectId }),
        trustWallet({ chains, projectId }),
      ],
    },
  ]);
};

const getChains = (network: number) => {
  const activeChain =
    Object.values(chains).find(({ id }) => id === network) ?? chains.mainnet;
  const providers = getProviders(network);
  return configureChains([activeChain], providers);
};

export const Web3Provider = ({
  network,
  children,
}: {
  network: number;
  children: ReactNode;
}) => {
  const { publicClient, chains } = useMemo(() => {
    return getChains(network);
  }, [network]);

  const connectors = useMemo(() => {
    return getConnectors(chains);
  }, [chains]);

  const wagmiConfig = useMemo(() => {
    return createConfig({
      autoConnect: true,
      publicClient,
      connectors,
    });
  }, [connectors, publicClient]);

  return (
    <WagmiConfig config={wagmiConfig}>
      <RainbowKitProvider
        chains={chains}
        modalSize="compact"
        theme={darkTheme({
          accentColor: '#FD247F',
          borderRadius: 'small',
        })}
        showRecentTransactions={true}
        coolMode={true}
        appInfo={{
          appName: 'NFTX',
        }}
      >
        {children}
      </RainbowKitProvider>
    </WagmiConfig>
  );
};
